import { Box, Card, Flex } from '@chakra-ui/react'
import { createFileRoute } from '@tanstack/react-router'
import {
  applyEdgeChanges,
  applyNodeChanges,
  Background,
  Controls,
  type Edge,
  type EdgeChange,
  MarkerType,
  type Node,
  type NodeChange,
  Position,
  ReactFlow,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { useCallback, useState } from 'react'
import IncrementCountNode from '~/components/nodes/datahub/IncrementCountNode'
import InputMessageNode from '~/components/nodes/datahub/InputMessageNode'
import OutputMessageNode from '~/components/nodes/datahub/OutputMessageNode'
import SchemaValidationNode from '~/components/nodes/datahub/SchemaValidationNode'
import TransformationNode from '~/components/nodes/datahub/TransformationNode'
import { PageHeader } from '~/components/shell'

export const Route = createFileRoute('/_app/dhanonymizeflow')({
  component: DHAnonymizeFlowPage,
})

const schema = `
\`\`\`json
{
  "type": "object",
  "properties": {
    "id": {
      "type": "string"
    },
    "name": {
      "type": "string"
    },
    "surname": {
      "type": "string"
    },
    "dateOfBirth": {
      "type": "string",
      "format": "date"
    },
    "address": {
      "type": "object",
      "properties": {
        "street": {
          "type": "string"
        },
        "city": {
          "type": "string"
        },
        "zipCode": {
          "type": "string"
        },
        "country": {
          "type": "string"
        }
      },
      "required": ["street", "city", "zipCode", "country"]
    }
  },
  "required": [
    "name",
    "surname",
    "dateOfBirth",
    "address"
  ]
}
`

const script = `
\`\`\`typescript
function maskString(inputString) {
    if (inputString.length <= 2) {
        return inputString;
    }

    const firstChar = inputString.charAt(0);
    const lastChar = inputString.charAt(inputString.length - 1);

    const maskedChars = '*'.repeat(inputString.length - 2);

    const maskedString = firstChar + maskedChars + lastChar;

    return maskedString;
}

function extractYearFromDate(dateString) {
    const year = dateString.split('-')[0];
    return "\${year}-01-01";
}

function transform(publish, context) {
    publish.payload = {
    	"id": publish.payload.id,
    	"name": publish.payload.name,
        "surname": maskString(publish.payload.surname),
        "dateOfBirth": extractYearFromDate(publish.payload.dateOfBirth),
        "address": {
            "street": "[REDACTED]",
            "city": publish.payload.address.city,
            "zipCode": "[REDACTED]",
            "country": publish.payload.address.country
        }
    }

    return publish;
}
`

const inputMessage = `
\`\`\`json
{
  "name": "Martin",
  "surname": "McFly",
  "dateOfBirth": "1986-05-02",
  "address": {
    "street": "9303 Lyon Drive",
    "city": "Hill Valley",
    "zipCode": "95420",
    "country": "USA"
  }
}
`

const outputMessage = `
\`\`\`json
{
  "name": "Martin",
  "surname": "M***y",
  "dateOfBirth": "1986-01-01",
  "address": {
    "zipCode": "[REDACTED]",
    "country": "USA",
    "city": "Hill Valley",
    "street": "[REDACTED]"
  }
}
`

const initNodes: Node[] = [
  // Input
  {
    id: '1',
    data: {
      label: 'InputMessage',
      topic: 'SiliValley/Customers/Active/3213333',
      message: inputMessage,
    },
    type: 'inputMessageNode',
    sourcePosition: Position.Right,
    targetPosition: Position.Right,
    position: { x: 0, y: 100 },
  },

  // DataHub
  {
    id: '2',
    data: { label: 'Schema Validation', schema: schema },
    type: 'schemaValidationNode',
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    position: { x: 600, y: 0 },
  },
  {
    id: '3',
    data: {
      label: 'Log Error',
      logCounter: 'TemperatureSchemaValidationError',
      message: 'Schema validation failed',
    },
    type: 'incrementCountNode',
    sourcePosition: Position.Top,
    targetPosition: Position.Top,
    position: { x: 800, y: 1300 },
  },
  {
    id: '4',
    data: { label: 'Transformation', script: script },
    type: 'transformationNode',
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    position: { x: 1500, y: 0 },
  },

  // Output
  {
    id: '5',
    data: {
      label: 'OutputMessage',
      topic: 'SiliValley/Customers/Active/3213333',
      message: outputMessage,
    },
    type: 'outputMessageNode',
    targetPosition: Position.Left,
    sourcePosition: Position.Left,
    position: { x: 2400, y: 100 },
  },
]

const initEdges: Edge[] = [
  {
    id: '12',
    source: '1',
    target: '2',
    label: 'De-serialize',
    style: { stroke: '#037DA5', strokeWidth: 5 },
    animated: true,
    markerEnd: { type: MarkerType.ArrowClosed, color: '#037DA5' },
  },
  {
    id: '23',
    source: '2',
    sourceHandle: 'b',
    target: '3',
    label: 'Error',
    style: { stroke: '#e91e63', strokeWidth: 5 },
    animated: true,
    markerEnd: { type: MarkerType.ArrowClosed, color: '#e91e63' },
  },
  {
    id: '24',
    source: '2',
    sourceHandle: 'a',
    target: '4',
    label: 'Success',
    style: { stroke: '#10b981', strokeWidth: 5 },
    animated: true,
    markerEnd: { type: MarkerType.ArrowClosed, color: '#10b981' },
  },
  {
    id: '45',
    source: '4',
    target: '5',
    label: 'Serialize',
    style: { stroke: '#037DA5', strokeWidth: 5 },
    animated: true,
    markerEnd: { type: MarkerType.ArrowClosed, color: '#037DA5' },
  },
]

const nodeTypes = {
  inputMessageNode: InputMessageNode,
  schemaValidationNode: SchemaValidationNode,
  incrementCountNode: IncrementCountNode,
  transformationNode: TransformationNode,
  outputMessageNode: OutputMessageNode,
}

function DHAnonymizeFlowPage() {
  const [nodes, setNodes] = useState<Node[]>(initNodes)
  const [edges, setEdges] = useState<Edge[]>(initEdges)

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => setNodes((nds) => applyNodeChanges(changes, nds)),
    [],
  )
  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    [],
  )

  return (
    <Flex direction="column" h="100%" gap={4}>
      <PageHeader
        title="Data Anonymization Flow"
        subtitle="HiveMQ DataHub pipeline for anonymizing sensitive customer data"
      />

      <Card.Root flex={1} minH={0} overflow="hidden">
        <Box h="100%" w="100%">
          <ReactFlow
            nodes={nodes}
            onNodesChange={onNodesChange}
            edges={edges}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            fitView
            className="flow-customization"
          >
            <Background />
            <Controls />
          </ReactFlow>
        </Box>
      </Card.Root>
    </Flex>
  )
}
